import { useState } from "react";
import { Hash, Plus, Search, LogOut, MessageCircle, Shield, Image as ImageIcon, Loader2, X, ChevronDown, ChevronRight, Layers } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserAvatar } from "@/components/Avatar";
import GroupManager from "@/components/GroupManager";
import EmbedCodeDialog, { EmbedIconButton } from "@/components/EmbedCodeDialog";
import { createRoom, setRoomAdmin, setRoomLogo, uploadImage, mediaUrl } from "@/lib/chatApi";

export default function Sidebar({ rooms, groups = [], moderators = [], activeRoom, unread = {}, onSelect, user, onLogout, onRoomCreated, onRoomUpdated, refreshGroups }) {
  const [embedTarget, setEmbedTarget] = useState(null);
  const isGlobalMod = user.isAdmin && moderators.includes(user.username);
  const logoRooms = user.isAdmin ? rooms.filter((r) => isGlobalMod || r.admin_username === user.username) : [];
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newLogo, setNewLogo] = useState("");
  const [newAdminUser, setNewAdminUser] = useState("");
  const [newAdminPass, setNewAdminPass] = useState("");
  const [creating, setCreating] = useState(false);
  const [uploadingNew, setUploadingNew] = useState(false);

  const [manageOpen, setManageOpen] = useState(false);
  const [manageRoomId, setManageRoomId] = useState("");
  const [manageUser, setManageUser] = useState("");
  const [managePass, setManagePass] = useState("");
  const [managing, setManaging] = useState(false);

  const [logoOpen, setLogoOpen] = useState(false);
  const [logoRoomId, setLogoRoomId] = useState("");
  const [logoValue, setLogoValue] = useState("");
  const [savingLogo, setSavingLogo] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleUpload = async (file, setUploading, setValue) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setValue(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunggah logo");
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error("Nama room wajib diisi");
      return;
    }
    if ((newAdminUser && !newAdminPass) || (!newAdminUser && newAdminPass)) {
      toast.error("Isi username DAN password admin, atau kosongkan keduanya");
      return;
    }
    setCreating(true);
    try {
      const room = await createRoom(newName.trim(), newDesc.trim(), user.username, newAdminUser.trim() || null, newAdminPass || null, newLogo || null);
      onRoomCreated(room);
      setDialogOpen(false);
      setNewName("");
      setNewDesc("");
      setNewLogo("");
      setNewAdminUser("");
      setNewAdminPass("");
      toast.success("Room berhasil dibuat");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat room");
    } finally {
      setCreating(false);
    }
  };

  const openLogoDialog = () => {
    const first = logoRooms.find((x) => x.id === activeRoom?.id)?.id || logoRooms[0]?.id || "";
    setLogoRoomId(first);
    const r = logoRooms.find((x) => x.id === first);
    setLogoValue(r?.logo || "");
    setLogoOpen(true);
  };

  const handleSaveLogo = async () => {
    if (!logoRoomId) {
      toast.error("Pilih room dulu");
      return;
    }
    setSavingLogo(true);
    try {
      const updated = await setRoomLogo(user.token, logoRoomId, logoValue || null);
      onRoomUpdated?.(updated);
      setLogoOpen(false);
      toast.success("Logo room diperbarui");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan logo");
    } finally {
      setSavingLogo(false);
    }
  };

  const handleManage = async () => {
    if (!manageRoomId || !manageUser.trim() || !managePass) {
      toast.error("Pilih room dan isi username + password admin");
      return;
    }
    setManaging(true);
    try {
      await setRoomAdmin(user.token, manageRoomId, manageUser.trim(), managePass);
      setManageOpen(false);
      setManageUser("");
      setManagePass("");
      toast.success("Admin room diperbarui");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengatur admin");
    } finally {
      setManaging(false);
    }
  };

  return (
    <div className="w-full md:w-80 border-r border-slate-200 dark:border-slate-800/80 bg-white/80 dark:bg-slate-900/60 backdrop-blur-xl flex flex-col h-full shrink-0">
      <div className="h-16 px-4 flex items-center gap-2.5 border-b border-slate-200 dark:border-slate-800/80 shrink-0">
        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center">
          <MessageCircle className="w-5 h-5 text-white" />
        </div>
        <span className="text-lg font-extrabold text-slate-900 dark:text-white font-heading tracking-tight">RuangChat</span>
      </div>

      <div className="p-3 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="search-rooms-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari room..."
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>

        <div className="flex items-center justify-between px-1">
          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Ruang Obrolan</span>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="add-room-button"
                className="p-1 rounded-md text-slate-400 hover:text-indigo-500 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
              <DialogHeader>
                <DialogTitle>Buat Room Baru</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <input
                  data-testid="new-room-name-input"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Nama room"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                />
                <input
                  data-testid="new-room-desc-input"
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder="Deskripsi (opsional)"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                />
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                    {newLogo ? (
                      <img src={mediaUrl(newLogo)} alt="logo" className="w-full h-full object-cover" data-testid="new-room-logo-preview" />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <label
                    data-testid="new-room-logo-label"
                    className="flex-1 cursor-pointer flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-slate-300 dark:border-slate-600 text-xs font-semibold text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
                  >
                    {uploadingNew ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                    {uploadingNew ? "Mengunggah..." : newLogo ? "Ganti logo room" : "Unggah logo room (opsional)"}
                    <input
                      data-testid="new-room-logo-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleUpload(e.target.files?.[0], setUploadingNew, setNewLogo)}
                    />
                  </label>
                  {newLogo && (
                    <button
                      data-testid="new-room-logo-remove"
                      onClick={() => setNewLogo("")}
                      className="p-2 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                      title="Hapus logo"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-400">Kamu akan menjadi moderator room ini dan bisa menyematkan pesan.</p>
                <div className="pt-2 border-t border-slate-200 dark:border-slate-800 space-y-3">
                  <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Admin room (opsional)</p>
                  <input
                    data-testid="new-room-admin-user"
                    value={newAdminUser}
                    onChange={(e) => setNewAdminUser(e.target.value)}
                    placeholder="Username admin"
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  />
                  <input
                    data-testid="new-room-admin-pass"
                    type="password"
                    value={newAdminPass}
                    onChange={(e) => setNewAdminPass(e.target.value)}
                    placeholder="Password admin"
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  />
                </div>
              </div>
              <DialogFooter>
                <button
                  data-testid="create-room-submit"
                  onClick={handleCreate}
                  disabled={creating}
                  className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm transition-all disabled:opacity-60"
                >
                  {creating ? "Membuat..." : "Buat Room"}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 space-y-1" data-testid="rooms-list">
        {(() => {
          const matchSearch = (r) => r.name.toLowerCase().includes(search.toLowerCase());
          const byId = Object.fromEntries(rooms.map((r) => [r.id, r]));
          const groupedIds = new Set(groups.flatMap((g) => g.room_ids || []));
          const ungrouped = rooms.filter((r) => !groupedIds.has(r.id)).filter(matchSearch);

          const renderRoomItem = (room) => {
            const active = activeRoom?.id === room.id;
            return (
              <div key={room.id} className="relative group">
              <button
                data-testid={`room-item-${room.id}`}
                onClick={() => onSelect(room)}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl transition-all text-left ${
                  active
                    ? "bg-indigo-100 dark:bg-indigo-600/20 border border-indigo-300 dark:border-indigo-500/40 text-indigo-900 dark:text-white"
                    : "hover:bg-slate-100 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-300 border border-transparent"
                }`}
              >
                {room.logo ? (
                  <img
                    src={mediaUrl(room.logo)}
                    alt={room.name}
                    data-testid={`room-logo-${room.id}`}
                    className="w-7 h-7 rounded-lg object-cover shrink-0 border border-slate-200 dark:border-slate-700"
                  />
                ) : (
                  <Hash className={`w-4 h-4 shrink-0 ${active ? "text-indigo-500 dark:text-indigo-400" : "text-slate-400 dark:text-slate-500"}`} />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{room.name}</p>
                  {room.description && <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{room.description}</p>}
                </div>
                {!active && unread[room.id] > 0 && (
                  <span
                    data-testid={`unread-badge-${room.id}`}
                    className="ml-auto shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-indigo-500 text-white text-[10px] font-bold flex items-center justify-center"
                  >
                    {unread[room.id] > 99 ? "99+" : unread[room.id]}
                  </span>
                )}
                {user.isAdmin && <span className="w-6 shrink-0" />}
              </button>
              {user.isAdmin && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <EmbedIconButton
                    testId={`room-embed-${room.id}`}
                    title="Kode embed room"
                    onClick={() => setEmbedTarget({ label: room.name, query: `room=${room.id}&lock=1`, hint: "Widget dikunci ke room ini. Tempel sebelum tag </body> di website Anda." })}
                  />
                </div>
              )}
              </div>
            );
          };

          return (
            <>
              {groups.map((g) => {
                const gRooms = (g.room_ids || []).map((id) => byId[id]).filter(Boolean).filter(matchSearch);
                if (gRooms.length === 0) return null;
                const isCollapsed = collapsed[g.id];
                return (
                  <div key={g.id} className="pt-1" data-testid={`group-section-${g.id}`}>
                    <div className="relative group">
                    <button
                      data-testid={`group-toggle-${g.id}`}
                      onClick={() => setCollapsed((c) => ({ ...c, [g.id]: !c[g.id] }))}
                      className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 hover:text-indigo-500 transition-colors"
                    >
                      {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      <Layers className="w-3.5 h-3.5" />
                      <span className="truncate">{g.name}</span>
                      <span className="ml-auto text-[10px] font-semibold text-slate-400">{gRooms.length}</span>
                      {user.isAdmin && <span className="w-6 shrink-0" />}
                    </button>
                    {user.isAdmin && (
                      <div className="absolute right-1 top-1/2 -translate-y-1/2">
                        <EmbedIconButton
                          testId={`group-embed-${g.id}`}
                          title="Kode embed group"
                          onClick={() => setEmbedTarget({ label: g.name, query: `group=${g.id}`, hint: "Widget hanya menampilkan room di group ini. Tempel sebelum tag </body> di website Anda." })}
                        />
                      </div>
                    )}
                    </div>
                    {!isCollapsed && <div className="space-y-1">{gRooms.map(renderRoomItem)}</div>}
                  </div>
                );
              })}
              {ungrouped.length > 0 && (
                <div className="pt-1">
                  {groups.length > 0 && (
                    <p className="px-2 py-1.5 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                      <Hash className="w-3.5 h-3.5" /> Lainnya
                    </p>
                  )}
                  <div className="space-y-1">{ungrouped.map(renderRoomItem)}</div>
                </div>
              )}
            </>
          );
        })()}
      </div>

      <div className="p-3 border-t border-slate-200 dark:border-slate-800/80 shrink-0 space-y-2">
        {user.isAdmin && (
          <GroupManager
            rooms={rooms}
            groups={groups}
            token={user.token}
            onChanged={refreshGroups}
          />
        )}
        <EmbedCodeDialog target={embedTarget} onClose={() => setEmbedTarget(null)} />
        {logoRooms.length > 0 && (
        <Dialog open={logoOpen} onOpenChange={setLogoOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="manage-logo-button"
              onClick={openLogoDialog}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-500/30 hover:bg-indigo-500/20 transition-colors"
            >
              <ImageIcon className="w-4 h-4" /> Atur Logo Room
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
            <DialogHeader>
              <DialogTitle>Atur Logo Room</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <select
                data-testid="logo-room-select"
                value={logoRoomId}
                onChange={(e) => {
                  setLogoRoomId(e.target.value);
                  setLogoValue(logoRooms.find((x) => x.id === e.target.value)?.logo || "");
                }}
                className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              >
                <option value="">Pilih room...</option>
                {logoRooms.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                  {logoValue ? (
                    <img src={mediaUrl(logoValue)} alt="logo" className="w-full h-full object-cover" data-testid="logo-dialog-preview" />
                  ) : (
                    <ImageIcon className="w-5 h-5 text-slate-400" />
                  )}
                </div>
                <label
                  data-testid="logo-dialog-upload-label"
                  className="flex-1 cursor-pointer flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-slate-300 dark:border-slate-600 text-xs font-semibold text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
                >
                  {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                  {uploadingLogo ? "Mengunggah..." : logoValue ? "Ganti logo" : "Unggah logo"}
                  <input
                    data-testid="logo-dialog-file-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleUpload(e.target.files?.[0], setUploadingLogo, setLogoValue)}
                  />
                </label>
                {logoValue && (
                  <button
                    data-testid="logo-dialog-remove"
                    onClick={() => setLogoValue("")}
                    className="p-2 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                    title="Hapus logo"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-400">Hanya admin room tersebut (atau moderator global) yang bisa mengubah logo.</p>
            </div>
            <DialogFooter>
              <button
                data-testid="logo-dialog-submit"
                onClick={handleSaveLogo}
                disabled={savingLogo || uploadingLogo}
                className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm transition-all disabled:opacity-60"
              >
                {savingLogo ? "Menyimpan..." : "Simpan Logo"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
        {user.isAdmin && (
          <Dialog open={manageOpen} onOpenChange={setManageOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="manage-admin-button"
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 transition-colors"
              >
                <Shield className="w-4 h-4" /> Kelola Admin Room
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
              <DialogHeader>
                <DialogTitle>Kelola Admin Room</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <select
                  data-testid="manage-room-select"
                  value={manageRoomId}
                  onChange={(e) => setManageRoomId(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                >
                  <option value="">Pilih room...</option>
                  {rooms.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}{r.admin_username ? ` (admin: ${r.admin_username})` : ""}</option>
                  ))}
                </select>
                <input
                  data-testid="manage-admin-user"
                  value={manageUser}
                  onChange={(e) => setManageUser(e.target.value)}
                  placeholder="Username admin"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                />
                <input
                  data-testid="manage-admin-pass"
                  type="password"
                  value={managePass}
                  onChange={(e) => setManagePass(e.target.value)}
                  placeholder="Password admin"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                />
                <p className="text-xs text-slate-400">Jika username admin sudah ada, password harus cocok. Jika belum, akun admin baru dibuat.</p>
              </div>
              <DialogFooter>
                <button
                  data-testid="manage-admin-submit"
                  onClick={handleManage}
                  disabled={managing}
                  className="px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-semibold text-sm transition-all disabled:opacity-60"
                >
                  {managing ? "Menyimpan..." : "Simpan Admin"}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        <div className="flex items-center gap-3 px-1" data-testid="user-profile-badge">
          <UserAvatar username={user.username} avatar={user.avatar} size={40} showStatus status="online" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900 dark:text-white truncate flex items-center gap-1.5">
              {user.username}
              {user.isAdmin && <Shield className="w-3.5 h-3.5 text-amber-500" />}
            </p>
            <p className={`text-xs ${user.isAdmin ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400"}`}>
              {user.isAdmin ? "Admin" : "Online"}
            </p>
          </div>
          <button
            data-testid="logout-button"
            onClick={onLogout}
            className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
